import React, { Fragment } from 'react';
import { styled } from 'dripsy';
import { View } from 'react-native';
import { observer } from 'mobx-react-lite';

import { FlatList, IFlatListProps } from '../common/FlatList';
import { RefreshControl } from '../common/RefreshControl';
import { Loader } from '../common/Loader';
import { Separator } from '../common/Separator';
import {
  EmptyResultTypeEnum,
  EmptyResultView,
} from './EmptyResultView';

const style = {
  flex: 1,
};

const LoaderContainer = styled(View)({
  flex: 1,
  alignItems: 'center',
  justifyContent: 'center',
});

export interface IListViewProps<TItem> extends IFlatListProps<TItem> {
  isLoading?: boolean;
  isLoadingMore?: boolean;
  isRefreshing?: boolean;
  onRefresh?(): void;
  emptyResultType?: EmptyResultTypeEnum;
}

function ListViewInner<TItem>({
  isLoading,
  isLoadingMore,
  isRefreshing = false,
  onRefresh,
  emptyResultType,
  ...props
}: IListViewProps<TItem>) {
  if (isLoading) {
    return (
      <LoaderContainer>
        <Loader size="large" />
      </LoaderContainer>
    );
  }

  return (
    <FlatList
      style={style}
      refreshControl={
        onRefresh && (
          <RefreshControl
            refreshing={isRefreshing}
            onRefresh={onRefresh}
          />
        )
      }
      ListFooterComponent={() =>
        isLoadingMore ? (
          <Fragment>
            <Separator />
            <Loader size="large" />
            <Separator />
          </Fragment>
        ) : (
          <Separator />
        )
      }
      ListEmptyComponent={() => (
        <EmptyResultView type={emptyResultType} />
      )}
      {...props}
    />
  );
}

// Code with ...Inner allow for us to pass generic to ListView component
export const ListView = observer(ListViewInner) as <T>(
  props: IListViewProps<T>,
) => ReturnType<typeof ListViewInner>;
